import { z } from 'zod';
import { createPaginationSchema, objectIdSchema } from './common.schema';

/**
 * Contract for the `_template` reference module.
 *
 * This is a throwaway entity that exists only to exercise every layer end to end.
 * It is the shape a real module's schema file should have: one base schema, with
 * every other schema derived from it by `.extend` / `.pick` / `.omit` / `.partial`
 * (CLAUDE.md §6). Nothing here is hand-written twice.
 */

export const TEMPLATE_ITEM_STATUSES = ['draft', 'active', 'archived'] as const;
export type TemplateItemStatus = (typeof TEMPLATE_ITEM_STATUSES)[number];

/** The editable fields. Every other schema in this file derives from this one. */
export const templateItemBaseSchema = z.object({
  displayName: z.string().trim().min(1, 'Name is required').max(120),
  status: z.enum(TEMPLATE_ITEM_STATUSES),
  quantity: z.number().int().min(0).max(1_000_000),
  notes: z.string().trim().max(2_000).optional(),
});

/** POST body. `status` defaults so the client may omit it. */
export const createTemplateItemSchema = templateItemBaseSchema.extend({
  status: z.enum(TEMPLATE_ITEM_STATUSES).default('draft'),
});

/** PATCH body — every field optional, but the body may not be empty. */
export const updateTemplateItemSchema = templateItemBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

/** What the API returns. Server-owned fields are never accepted on input. */
export const templateItemSchema = templateItemBaseSchema.extend({
  id: objectIdSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/**
 * GET /template query. `sortBy` is an allowlist, never an open string — see
 * `createPaginationSchema`. Add a field here only when it is indexed on the model.
 */
export const TEMPLATE_ITEM_SORT_FIELDS = ['createdAt', 'updatedAt', 'displayName', 'quantity'] as const;

export const listTemplateItemsQuerySchema = createPaginationSchema(TEMPLATE_ITEM_SORT_FIELDS, 'createdAt').extend({
  status: z.enum(TEMPLATE_ITEM_STATUSES).optional(),
  search: z.string().trim().min(1).max(120).optional(),
});
